import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    ReloadOutlined,
    UnorderedListOutlined,
    ClockCircleOutlined,
    ClearOutlined,
    FileExcelOutlined,
    CloseOutlined,
} from "@ant-design/icons";
import {
    Button,
    Card,
    Table,
    Tag,
    message,
    Modal,
    Input,
    Space,
    Tooltip,
    Select,
    DatePicker,
    Row,
    Col,
    Typography,
    Badge,
} from "antd";
import { useState, useContext, useEffect } from "react";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import { NavigationContext } from "../../context/NavigationContext";
import dayjs from "dayjs";
import * as XLSX from "xlsx";

const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

export default function StatusUpdation() {
    const { authData, backUrl } = useContext(AuthContext);
    const { navigationParams, clearNavigationParams } = useContext(NavigationContext);
    const isMobile = window.innerWidth < 768;

    // State management
    const [dataSource, setDataSource] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    // ✅ Active tab state: "pending" | "approved" | "rejected"
    const [activeTab, setActiveTab] = useState("pending");

    // ✅ Filter reference data
    // NOTE: departments/designations are now cascading lists — they only
    // contain options relevant to the currently selected organisation(s) /
    // department(s), fetched fresh whenever that selection changes.
    const [organisations, setOrganisations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);

    // ✅ Requested Status options — the set of statuses a status-change
    // request can move an employee's attendance to. Kept in sync with
    // getStatusColor's key list so the dropdown, the badges, and the
    // filter options never drift apart.
    const requestedStatusOptions = [
        "PRESENT",
        "PERMISSION",
        "ABSENT",
        "HOLIDAY",
        "Weekoff",
        "Compoff",
        "CASUAL LEAVE",
        "SICK LEAVE",
        "HALFDAY_LEAVE",
    ];

    // ✅ Filter states — text filters match the backend's icontains fields
    const [employeeNameFilter, setEmployeeNameFilter] = useState("");
    const [empCodeFilter, setEmpCodeFilter] = useState("");
    const [selectedOrganisations, setSelectedOrganisations] = useState([]);
    const [selectedDepartments, setSelectedDepartments] = useState([]);
    const [selectedDesignations, setSelectedDesignations] = useState([]);
    const [selectedRequestedStatuses, setSelectedRequestedStatuses] = useState([]);
    const [dateRange, setDateRange] = useState([null, null]);

    // ✅ Total record counts per tab — shown as badges over the
    // Pending/Approved/Rejected buttons. Kept independent of pagination
    // (currentPage/pageSize) but responsive to whatever filters are active,
    // so the badge always reflects "how many match my current filters".
    const [tabCounts, setTabCounts] = useState({
        pending: 0,
        approved: 0,
        rejected: 0,
    });

    // Modal state for approval/rejection
    // actionType is one of: "approve" | "reject" (single row) or
    // "bulk-approve" | "bulk-reject" (selected rows via checkboxes)
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [actionType, setActionType] = useState("");
    const [remarks, setRemarks] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // ✅ Bulk selection — row keys (ids) checked via the table's checkboxes.
    // Used for both bulk approve/reject and Export Excel.
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    // selectedRowKeys only tracks ids; the table only ever renders one page
    // of row objects at a time, so this map keeps the actual record for
    // every checked id (merged in across page changes) — needed so Export
    // Excel can output full rows for a selection that spans multiple pages.
    const [selectedRecordsMap, setSelectedRecordsMap] = useState({});
    const [exporting, setExporting] = useState(false);

    const isBulkAction = actionType === "bulk-approve" || actionType === "bulk-reject";
    const isApprovingAction = actionType === "approve" || actionType === "bulk-approve";

    // ✅ Tab configuration — colors, labels, and which endpoint each tab hits
    const tabConfig = {
        pending: {
            label: "Pending",
            color: "#faad14",       // yellow
            bgActive: "#faad14",
            icon: <ClockCircleOutlined />,
            endpoint: "pending-list",
        },
        approved: {
            label: "Approved",
            color: "#52c41a",       // green
            bgActive: "#52c41a",
            icon: <CheckCircleOutlined />,
            endpoint: "all-history",
        },
        rejected: {
            label: "Rejected",
            color: "#ff4d4f",       // red
            bgActive: "#ff4d4f",
            icon: <CloseCircleOutlined />,
            endpoint: "all-history",
        },
    };

    // Status color mapping
    const getStatusColor = (status) => {
        const statusColors = {
            "PRESENT": "green",
            "PERMISSION": "orange",
            "ABSENT": "red",
            "HOLIDAY": "blue",
            "Weekoff": "purple",
            "Compoff": "cyan",
            "CASUAL LEAVE": "magenta",
            "SICK LEAVE": "volcano",
            "HALFDAY_LEAVE": "geekblue",
        };
        return statusColors[status] || "default";
    };

    // ✅ Fetch top-level organisation list (not dependent on any other filter)
    const fetchOrganisations = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            setOrganisations(res.data.results || []);
        } catch (err) {
            console.error("Failed to load organisations");
        }
    };

    // ✅ Fetch departments scoped to the selected organisation(s).
    // Called whenever selectedOrganisations changes; returns [] (cleared)
    // when no organisation is selected, since departments should not be
    // browsable until an organisation is chosen.
    const fetchDepartmentsForOrgs = async (orgIds) => {
        if (!orgIds || orgIds.length === 0) {
            setDepartments([]);
            return;
        }
        try {
            const params = new URLSearchParams();
            orgIds.forEach((id) => params.append("organisation", id));
            const res = await axios.get(
                `${backUrl}/api/emp/departments/?${params.toString()}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            setDepartments(res.data.results || []);
        } catch (err) {
            console.error("Failed to load departments");
            setDepartments([]);
        }
    };

    // ✅ Fetch designations scoped to the selected department(s).
    // Called whenever selectedDepartments changes; returns [] (cleared)
    // when no department is selected.
    const fetchDesignationsForDepts = async (deptIds) => {
        if (!deptIds || deptIds.length === 0) {
            setDesignations([]);
            return;
        }
        try {
            const params = new URLSearchParams();
            deptIds.forEach((id) => params.append("department", id));
            const res = await axios.get(
                `${backUrl}/api/emp/designations/?${params.toString()}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            setDesignations(res.data.results || []);
        } catch (err) {
            console.error("Failed to load designations");
            setDesignations([]);
        }
    };

    useEffect(() => {
        fetchOrganisations();
        // Departments/designations start empty — they're populated only
        // once an organisation (then a department) is selected.
    }, []);

    // ✅ Builds the URLSearchParams shared by every tab: just the active
    // filters, with no page/page_size/outcome — those get appended by
    // each caller (fetchRequests for the current tab's data, fetchTabCounts
    // for all three tabs' totals) on top of this common base.
    const buildFilterParams = () => {
        const params = new URLSearchParams();

        if (employeeNameFilter.trim()) {
            params.append('employee_name', employeeNameFilter.trim());
        }
        if (empCodeFilter.trim()) {
            params.append('emp_code', empCodeFilter.trim());
        }
        if (selectedOrganisations.length > 0) {
            selectedOrganisations.forEach((orgId) => {
                params.append('organisation', orgId);
            });
        }
        if (selectedDepartments.length > 0) {
            selectedDepartments.forEach((deptId) => {
                params.append('department', deptId);
            });
        }
        if (selectedDesignations.length > 0) {
            selectedDesignations.forEach((desgId) => {
                params.append('designation', desgId);
            });
        }
        if (selectedRequestedStatuses.length > 0) {
            selectedRequestedStatuses.forEach((status) => {
                params.append('pending_status_change', status);
            });
        }
        if (dateRange && dateRange[0] && dateRange[1]) {
            params.append('from_date', dayjs(dateRange[0]).format('YYYY-MM-DD'));
            params.append('to_date', dayjs(dateRange[1]).format('YYYY-MM-DD'));
        }

        return params;
    };

    // ✅ Fetch the total count for each tab (Pending / Approved / Rejected)
    // under the current filters, using page_size=1 since only the "count"
    // field from the paginated response is needed — not the records.
    const fetchTabCounts = async () => {
        try {
            const headers = { Authorization: `Bearer ${authData?.access}` };

            const pendingParams = buildFilterParams();
            pendingParams.append('page', 1);
            pendingParams.append('page_size', 1);

            const approvedParams = buildFilterParams();
            approvedParams.append('outcome', 'approved');
            approvedParams.append('page', 1);
            approvedParams.append('page_size', 1);

            const rejectedParams = buildFilterParams();
            rejectedParams.append('outcome', 'rejected');
            rejectedParams.append('page', 1);
            rejectedParams.append('page_size', 1);

            const [pendingRes, approvedRes, rejectedRes] = await Promise.all([
                axios.get(
                    `${backUrl}/api/hr/attendance-changes/pending-list/?${pendingParams.toString()}`,
                    { headers }
                ),
                axios.get(
                    `${backUrl}/api/hr/attendance-changes/all-history/?${approvedParams.toString()}`,
                    { headers }
                ),
                axios.get(
                    `${backUrl}/api/hr/attendance-changes/all-history/?${rejectedParams.toString()}`,
                    { headers }
                ),
            ]);

            const extractCount = (res) =>
                res.data.count ?? res.data.total_count ?? (res.data.results || res.data.data || []).length;

            setTabCounts({
                pending: extractCount(pendingRes),
                approved: extractCount(approvedRes),
                rejected: extractCount(rejectedRes),
            });
        } catch (err) {
            console.error("Failed to load tab counts", err);
        }
    };

    // ✅ Fetch requests — endpoint & params driven by active tab, filters sent to backend
    const fetchRequests = async () => {
        setLoading(true);
        try {
            const { endpoint } = tabConfig[activeTab];
            const params = buildFilterParams();
            params.append('page', currentPage);
            params.append('page_size', pageSize);

            // Approved/Rejected both use all-history; outcome splits them
            if (activeTab === 'approved') params.append('outcome', 'approved');
            if (activeTab === 'rejected') params.append('outcome', 'rejected');

            const url = `${backUrl}/api/hr/attendance-changes/${endpoint}/?${params.toString()}`;
            console.log("Fetching requests from:", url);

            const res = await axios.get(url, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });

            console.log("Backend Response:", res.data);

            // pending-list / all-history are paginated (results/count);
            // my-requests-style {data, total_count} isn't used here, but guard anyway
            const data = res.data.results || res.data.data || res.data;
            setDataSource(data);
            setTotalRecords(res.data.count ?? res.data.total_count ?? data.length);
        } catch (error) {
            if (error.response?.status === 404) {
                // Page no longer has data (e.g., filters changed, page moved) — reset to page 1
                setCurrentPage(1);
                return;
            }
            console.error("Error fetching requests:", error);
            message.error(`Failed to fetch ${activeTab} status change requests`);
            setDataSource([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    };

    // Initial load & Navigation Sync
    useEffect(() => {
        if (navigationParams?.status) {
            const navStatus = navigationParams.status.toLowerCase();
            if (["pending", "approved", "rejected"].includes(navStatus)) {
                setActiveTab(navStatus);
                setCurrentPage(1);
            }
            clearNavigationParams();
        }
    }, [navigationParams, clearNavigationParams]);

    // ✅ Refetch whenever tab, page, page size, or any filter changes
    useEffect(() => {
        fetchRequests();
    }, [
        currentPage,
        pageSize,
        activeTab,
        employeeNameFilter,
        empCodeFilter,
        selectedOrganisations,
        selectedDepartments,
        selectedDesignations,
        selectedRequestedStatuses,
        dateRange,
    ]);

    // ✅ Refresh the Pending/Approved/Rejected badge counts whenever a
    // filter changes. Deliberately excludes currentPage/pageSize/activeTab —
    // counts reflect totals across all three tabs, not the page currently
    // being viewed, so they shouldn't refetch on every page turn.
    useEffect(() => {
        fetchTabCounts();
    }, [
        employeeNameFilter,
        empCodeFilter,
        selectedOrganisations,
        selectedDepartments,
        selectedDesignations,
        selectedRequestedStatuses,
        dateRange,
    ]);

    // ✅ Handle table pagination change (page number AND page size)
    const handleTableChange = (pagination) => {
        const newPageSize = pagination.pageSize;
        const pageSizeChanged = newPageSize !== pageSize;

        if (pageSizeChanged) {
            // Reset to page 1 whenever page size changes — otherwise the
            // previously selected page number may not exist anymore for
            // the new page size.
            setPageSize(newPageSize);
            setCurrentPage(1);
        } else {
            setCurrentPage(pagination.current);
        }
    };

    // ✅ Handle tab click
    const handleTabClick = (tabKey) => {
        if (tabKey === activeTab) return;
        setActiveTab(tabKey);
        setCurrentPage(1); // reset to first page on tab switch
        setSelectedRowKeys([]); // selections don't carry over between tabs
        setSelectedRecordsMap({});
    };

    // ✅ Filter change handlers (each resets to page 1)
    const handleEmployeeNameChange = (e) => {
        setEmployeeNameFilter(e.target.value);
        setCurrentPage(1);
    };

    const handleEmpCodeChange = (e) => {
        setEmpCodeFilter(e.target.value);
        setCurrentPage(1);
    };

    // ✅ Organisation change — cascades down: resets department & designation
    // selections/lists, then loads departments scoped to the new org(s).
    const handleOrganisationChange = (value) => {
        setSelectedOrganisations(value);
        setSelectedDepartments([]);
        setSelectedDesignations([]);
        setDepartments([]);
        setDesignations([]);
        setCurrentPage(1);
        fetchDepartmentsForOrgs(value);
    };

    // ✅ Department change — cascades down: resets designation
    // selection/list, then loads designations scoped to the new dept(s).
    const handleDepartmentChange = (value) => {
        setSelectedDepartments(value);
        setSelectedDesignations([]);
        setDesignations([]);
        setCurrentPage(1);
        fetchDesignationsForDepts(value);
    };

    const handleDesignationChange = (value) => {
        setSelectedDesignations(value);
        setCurrentPage(1);
    };

    // ✅ Requested Status change — independent filter, no cascading needed
    const handleRequestedStatusChange = (value) => {
        setSelectedRequestedStatuses(value);
        setCurrentPage(1);
    };

    const handleDateRangeChange = (dates) => {
        setDateRange(dates || [null, null]);
        setCurrentPage(1);
    };

    const handleClearAllFilters = () => {
        setEmployeeNameFilter("");
        setEmpCodeFilter("");
        setSelectedOrganisations([]);
        setSelectedDepartments([]);
        setSelectedDesignations([]);
        setDepartments([]);
        setDesignations([]);
        setSelectedRequestedStatuses([]);
        setDateRange([null, null]);
        setCurrentPage(1);
    };

    const hasActiveFilters = () => {
        return (
            employeeNameFilter.trim() ||
            empCodeFilter.trim() ||
            selectedOrganisations.length > 0 ||
            selectedDepartments.length > 0 ||
            selectedDesignations.length > 0 ||
            selectedRequestedStatuses.length > 0 ||
            (dateRange && dateRange[0] && dateRange[1])
        );
    };

    // Open approval/rejection modal for a single row
    const handleActionClick = (record, action) => {
        setSelectedRequest(record);
        setActionType(action);
        setRemarks("");
        setIsModalVisible(true);
    };

    // ✅ Open approval/rejection modal for the checked rows in bulk.
    // actionType becomes "bulk-approve" / "bulk-reject" so the modal and
    // submit handler know to send selectedRowKeys instead of a single id.
    const handleBulkActionClick = (action) => {
        if (selectedRowKeys.length === 0) {
            message.warning("Please select at least one record");
            return;
        }
        setSelectedRequest(null);
        setActionType(action === "approve" ? "bulk-approve" : "bulk-reject");
        setRemarks("");
        setIsModalVisible(true);
    };

    // Submit approval or rejection — single row or bulk, based on actionType
    const handleSubmitAction = async () => {
        if (!isBulkAction && !selectedRequest) return;
        if (isBulkAction && selectedRowKeys.length === 0) return;

        const isApproving = actionType === "approve" || actionType === "bulk-approve";

        if (!remarks.trim()) {
            message.warning(`Please enter a reason for ${isApproving ? "approval" : "rejection"}`);
            return;
        }

        setSubmitting(true);
        try {
            let endpoint;
            let payload;

            if (isBulkAction) {
                endpoint = isApproving
                    ? `${backUrl}/api/hr/attendance-changes/bulk-approve/`
                    : `${backUrl}/api/hr/attendance-changes/bulk-reject/`;
                payload = {
                    ids: selectedRowKeys,
                    reason: remarks.trim(),
                };
            } else {
                endpoint = isApproving
                    ? `${backUrl}/api/hr/attendance-changes/${selectedRequest.id}/approve/`
                    : `${backUrl}/api/hr/attendance-changes/${selectedRequest.id}/reject/`;
                payload = {
                    reason: remarks.trim(),
                };
            }

            await axios.post(endpoint, payload, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });

            message.success(
                isBulkAction
                    ? `${selectedRowKeys.length} request${selectedRowKeys.length > 1 ? "s" : ""} ${isApproving ? "approved" : "rejected"} successfully!`
                    : `Status change request ${isApproving ? "approved" : "rejected"} successfully!`
            );

            setIsModalVisible(false);
            setRemarks("");
            setSelectedRequest(null);
            setActionType("");
            setSelectedRowKeys([]);
            setSelectedRecordsMap({});
            fetchRequests();
            fetchTabCounts();
            setCurrentPage(1);
        } catch (error) {
            console.error(`Error ${isApproving ? "approv" : "reject"}ing request(s):`, error);

            const errorMessage =
                error.response?.data?.detail ||
                error.response?.data?.message ||
                error.response?.data?.error ||
                `Failed to ${isApproving ? "approve" : "reject"} status change request${isBulkAction ? "s" : ""}`;

            message.error(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    // Handle modal cancel
    const handleModalCancel = () => {
        setIsModalVisible(false);
        setRemarks("");
        setSelectedRequest(null);
        setActionType("");
    };

    // ✅ Maps a raw record into the flat, human-readable row used in the
    // exported spreadsheet. Reviewer Remarks only makes sense outside the
    // Pending tab, so it's included conditionally.
    const buildExportRow = (record) => {
        const row = {
            "Requested By": record.requested_by_name || "",
            "Employee Code": record.emp_code || "",
            "Employee Name": record.employee_name || "",
            "Department": record.department_name || "",
            "Designation": record.designation_name || "",
            "Attendance Date": record.date
                ? dayjs(record.date).format("DD-MM-YYYY")
                : "",
            "Current Status": record.status || "",
            "Requested Status": record.pending_status_change || "",
            "Remarks": record.status_change_remarks || "",
        };
        if (activeTab !== "pending") {
            row["Reviewer Remarks"] = record.superuser_remarks || "";
        }
        return row;
    };

    // ✅ Export to Excel — built entirely on the frontend with SheetJS, no
    // backend export endpoint involved. If rows are checked, only those
    // (full objects tracked in selectedRecordsMap so it works across pages)
    // are exported. Otherwise every record matching the current tab/filters
    // is fetched in one page-size-uncapped call purely to gather the rows,
    // then the .xlsx file is generated and downloaded client-side.
    const handleExportExcel = async () => {
        setExporting(true);
        try {
            let recordsToExport = [];

            if (selectedRowKeys.length > 0) {
                recordsToExport = selectedRowKeys
                    .map((id) => selectedRecordsMap[id])
                    .filter(Boolean);
            } else {
                const { endpoint } = tabConfig[activeTab];
                const params = buildFilterParams();
                params.append("page", 1);
                params.append("page_size", totalRecords > 0 ? totalRecords : 10000);
                if (activeTab === "approved") params.append("outcome", "approved");
                if (activeTab === "rejected") params.append("outcome", "rejected");

                const res = await axios.get(
                    `${backUrl}/api/hr/attendance-changes/${endpoint}/?${params.toString()}`,
                    { headers: { Authorization: `Bearer ${authData?.access}` } }
                );
                recordsToExport = res.data.results || res.data.data || res.data || [];
            }

            if (recordsToExport.length === 0) {
                message.warning("No records to export");
                return;
            }

            const rows = recordsToExport.map(buildExportRow);
            const worksheet = XLSX.utils.json_to_sheet(rows);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(
                workbook,
                worksheet,
                activeTab.charAt(0).toUpperCase() + activeTab.slice(1)
            );
            XLSX.writeFile(
                workbook,
                `attendance_status_changes_${activeTab}_${dayjs().format("YYYYMMDD_HHmmss")}.xlsx`
            );

            message.success(`Exported ${recordsToExport.length} record${recordsToExport.length > 1 ? "s" : ""}`);
        } catch (error) {
            console.error("Error exporting to Excel:", error);
            message.error("Failed to export records");
        } finally {
            setExporting(false);
        }
    };

    // ✅ Row selection config for the table — shared by bulk approve/reject
    // and Export Excel. preserveSelectedRowKeys keeps checks intact across
    // page changes within the same tab. selectedRecordsMap is merged (not
    // replaced) on every change so full row data survives page turns even
    // though the table only ever hands back rows for the page it's on.
    const rowSelection = {
        selectedRowKeys,
        onChange: (keys, rows) => {
            setSelectedRowKeys(keys);
            setSelectedRecordsMap((prev) => {
                const next = {};
                keys.forEach((key) => {
                    const fromThisChange = rows.find((r) => r.id === key);
                    next[key] = fromThisChange || prev[key];
                });
                return next;
            });
        },
        preserveSelectedRowKeys: true,
    };

    // ✅ Base columns shown for all tabs
    const baseColumns = [
        {
            title: 'Requested By',
            dataIndex: "requested_by_name",
            key: 'requested_by'
        },
        {
            title: "Employee Code",
            dataIndex: "emp_code",
            key: "emp_code",
            width: 180,
        },
        {
            title: "Employee Name",
            dataIndex: "employee_name",
            key: "employee_name",
            width: 180,
        },
        {
            title: "Department",
            dataIndex: "department_name",
            key: "department",
            width: 150,
        },
        {
            title: "Designation",
            dataIndex: "designation_name",
            key: "designation",
            width: 150,
        },
        {
            title: "Attendance Date",
            dataIndex: "date",
            key: "date",
            width: 130,
        },
        {
            title: "Current Status",
            dataIndex: "status",
            key: "current_status",
            width: 130,
        },
        {
            title: "Requested Status",
            dataIndex: "pending_status_change",
            key: "new_status",
        },
        {
            title: "Remarks",
            dataIndex: "status_change_remarks",
            key: "remarks",
            width: 200,
        },
    ];

    // ✅ Extra columns depending on tab
    // const pendingActionColumn = {
    //     title: "Action",
    //     key: "action",
    //     width: 180,
    //     render: (_, record) => {
    //         // Hide buttons if user is not a superuser
    //         if (!authData?.is_superuser) {
    //             return (
    //                 <>

    //                 </>
    //             )
    //         }

    //         return (
    //             <Space size="small">
    //                 <Tooltip title="Approve">
    //                     <Button
    //                         type="primary"
    //                         icon={<CheckCircleOutlined />}
    //                         onClick={() => handleActionClick(record, "approve")}
    //                         style={{
    //                             backgroundColor: "#52c41a",
    //                             borderColor: "#52c41a",
    //                         }}
    //                         shape="round"
    //                     />
    //                 </Tooltip>

    //                 <Tooltip title="Reject">
    //                     <Button
    //                         danger
    //                         icon={<CloseCircleOutlined />}
    //                         onClick={() => handleActionClick(record, "reject")}
    //                         shape="round"
    //                     />
    //                 </Tooltip>
    //             </Space>
    //         );
    //     },
    // };

    // ✅ Approved/Rejected tabs show the superuser's remarks + a tag instead of actions
    const resolvedStatusColumn = {
        title: "Reviewer Remarks",
        dataIndex: "superuser_remarks",
        key: "superuser_remarks",
        width: 220,
        render: (val) => val || "-",
    };

    const columns = [...baseColumns, resolvedStatusColumn]

    // const columns = activeTab === "pending"
    //     // ? [...baseColumns, pendingActionColumn]
    //     : [...baseColumns, resolvedStatusColumn];

    return (
        <section>
            <center>
                {isMobile ? (
                    <p style={{ fontSize: "15px", textTransform: "uppercase", color: "#0e497b" }}>
                        Status Change Requests
                    </p>
                ) : (
                    <h5>
                        Attendance Status Change Requests
                    </h5>
                )}
            </center>

            {/* ✅ Filter Section */}
            <div style={{ margin: "0 20px" }}>
                <Card style={{ marginBottom: "16px" }}>
                    <Row gutter={[16, 16]} align="bottom">
                        <Col xs={24} sm={12} md={8} lg={5}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text strong>Employee Name</Text>
                                <Input
                                    placeholder="Search by name"
                                    value={employeeNameFilter}
                                    onChange={handleEmployeeNameChange}
                                    allowClear
                                />
                            </Space>
                        </Col>

                        <Col xs={24} sm={12} md={8} lg={4}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text strong>Employee Code</Text>
                                <Input
                                    placeholder="Search by code"
                                    value={empCodeFilter}
                                    onChange={handleEmpCodeChange}
                                    allowClear
                                />
                            </Space>
                        </Col>

                        <Col xs={24} sm={12} md={8} lg={4}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text strong>Organisation</Text>
                                <Select
                                    mode="multiple"
                                    placeholder="Select organisations"
                                    value={selectedOrganisations}
                                    onChange={handleOrganisationChange}
                                    style={{ width: "100%" }}
                                    allowClear
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {organisations.map((org) => (
                                        <Option key={org.id} value={org.id}>
                                            {org.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Space>
                        </Col>

                        <Col xs={24} sm={12} md={8} lg={4}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text strong>Department</Text>
                                <Select
                                    mode="multiple"
                                    placeholder={
                                        selectedOrganisations.length === 0
                                            ? "Select organisation first"
                                            : "Select departments"
                                    }
                                    value={selectedDepartments}
                                    onChange={handleDepartmentChange}
                                    style={{ width: "100%" }}
                                    allowClear
                                    showSearch
                                    disabled={selectedOrganisations.length === 0}
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {departments.map((dept) => (
                                        <Option key={dept.id} value={dept.id}>
                                            {dept.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Space>
                        </Col>

                        <Col xs={24} sm={12} md={8} lg={4}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text strong>Designation</Text>
                                <Select
                                    mode="multiple"
                                    placeholder={
                                        selectedDepartments.length === 0
                                            ? "Select department first"
                                            : "Select designations"
                                    }
                                    value={selectedDesignations}
                                    onChange={handleDesignationChange}
                                    style={{ width: "100%" }}
                                    allowClear
                                    showSearch
                                    disabled={selectedDepartments.length === 0}
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {designations.map((desg) => (
                                        <Option key={desg.id} value={desg.id}>
                                            {desg.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Space>
                        </Col>

                        <Col xs={24} sm={12} md={8} lg={4}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text strong>Requested Status</Text>
                                <Select
                                    mode="multiple"
                                    placeholder="Select requested status"
                                    value={selectedRequestedStatuses}
                                    onChange={handleRequestedStatusChange}
                                    style={{ width: "100%" }}
                                    allowClear
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {requestedStatusOptions.map((status) => (
                                        <Option key={status} value={status}>
                                            {status}
                                        </Option>
                                    ))}
                                </Select>
                            </Space>
                        </Col>

                        <Col xs={24} sm={12} md={8} lg={5}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text strong>Attendance Date Range</Text>
                                <RangePicker
                                    value={dateRange}
                                    onChange={handleDateRangeChange}
                                    format="DD-MM-YYYY"
                                    placeholder={["Start Date", "End Date"]}
                                    style={{ width: "100%" }}
                                />
                            </Space>
                        </Col>

                        {hasActiveFilters() && (
                            <Col xs={24} sm={12} md={8} lg={4}>
                                <Button
                                    icon={<ClearOutlined />}
                                    onClick={handleClearAllFilters}
                                    style={{ width: "100%" }}
                                >
                                    Clear Filters
                                </Button>
                            </Col>
                        )}
                    </Row>
                </Card>
            </div>

            {/* ✅ Tab Buttons — Pending / Approved / Rejected */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "12px",
                    margin: "16px 20px 0",
                    flexWrap: "wrap",
                }}
            >
                {Object.keys(tabConfig).map((tabKey) => {
                    const tab = tabConfig[tabKey];
                    const isActive = activeTab === tabKey;
                    return (
                        <Badge
                            key={tabKey}
                            count={tabCounts[tabKey]}
                            showZero
                            overflowCount={999}
                            style={{
                                backgroundColor: tab.color,
                                color: "#fff",
                                boxShadow: "0 0 0 1px #fff",
                            }}
                        >
                            <Button
                                icon={tab.icon}
                                onClick={() => handleTabClick(tabKey)}
                                style={{
                                    minWidth: "130px",
                                    fontWeight: 600,
                                    color: isActive ? "#fff" : tab.color,
                                    backgroundColor: isActive ? tab.bgActive : "#fff",
                                    borderColor: tab.color,
                                }}
                            >
                                {tab.label}
                            </Button>
                        </Badge>
                    );
                })}
            </div>

            {/* ✅ Bulk Actions / Export toolbar */}
            <div style={{ margin: "20px 20px 0" }}>
                <Card size="small" style={{ marginBottom: "0" }}>
                    <Row justify="space-between" align="middle" gutter={[12, 12]}>
                        <Col>
                            <Space wrap>
                                <Text strong>
                                    {selectedRowKeys.length > 0
                                        ? `${selectedRowKeys.length} record${selectedRowKeys.length > 1 ? "s" : ""} selected`
                                        : "No records selected"}
                                </Text>

                                {selectedRowKeys.length > 0 && (
                                    <Button
                                        size="small"
                                        icon={<CloseOutlined />}
                                        onClick={() => {
                                            setSelectedRowKeys([]);
                                            setSelectedRecordsMap({});
                                        }}
                                    >
                                        Clear Selection
                                    </Button>
                                )}
                            </Space>
                        </Col>

                        <Col>
                            <Space wrap>
                                {/* Bulk approve/reject only make sense on the Pending tab.
                                    Buttons are always visible here (disabled until rows are
                                    checked) so they're never missing from the toolbar. */}
                                {activeTab === "pending" && (
                                    <>
                                        <Button
                                            type="primary"
                                            icon={<CheckCircleOutlined />}
                                            disabled={selectedRowKeys.length === 0}
                                            onClick={() => handleBulkActionClick("approve")}
                                            style={{
                                                backgroundColor: "#52c41a",
                                                borderColor: "#52c41a",
                                            }}
                                        >
                                            Bulk Approve
                                        </Button>
                                        <Button
                                            danger
                                            icon={<CloseCircleOutlined />}
                                            disabled={selectedRowKeys.length === 0}
                                            onClick={() => handleBulkActionClick("reject")}
                                        >
                                            Bulk Reject
                                        </Button>
                                    </>
                                )}

                                <Button
                                    icon={<FileExcelOutlined />}
                                    loading={exporting}
                                    onClick={handleExportExcel}
                                    style={{ color: "#217346", borderColor: "#217346" }}
                                >
                                    Export Excel
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </Card>
            </div>

            {/* Requests Table */}
            <div style={{ margin: "20px" }}>
                <Table
                    columns={columns}
                    dataSource={dataSource}
                    bordered
                    loading={loading}
                    rowSelection={rowSelection}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: totalRecords,
                        showSizeChanger: true,
                        pageSizeOptions: ["10", "20", "50", "100"],
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
                    }}
                    scroll={{ x: "max-content" }}
                    rowKey={(record) => record.id}
                    onChange={handleTableChange}
                    components={{
                        header: {
                            cell: (props) => (
                                <th
                                    {...props}
                                    style={{
                                        backgroundColor: "#0e497b",
                                        color: "white",
                                    }}
                                >
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                />
            </div>

            {/* Approval/Rejection Modal — handles both single-row and bulk actions */}
            <Modal
                title={
                    isBulkAction
                        ? `Bulk ${isApprovingAction ? "Approve" : "Reject"} Status Change`
                        : `${isApprovingAction ? "Approve" : "Reject"} Status Change`
                }
                open={isModalVisible}
                onOk={handleSubmitAction}
                onCancel={handleModalCancel}
                confirmLoading={submitting}
                okText={isApprovingAction ? "Approve" : "Reject"}
                okButtonProps={{
                    danger: !isApprovingAction,
                    style: isApprovingAction
                        ? { backgroundColor: "#52c41a", borderColor: "#52c41a" }
                        : undefined,
                }}
                cancelText="Cancel"
                width={600}
            >
                {isBulkAction ? (
                    <div>
                        <div style={{ marginBottom: "20px" }}>
                            <p>
                                You are about to <strong>{isApprovingAction ? "approve" : "reject"}</strong>{" "}
                                <strong>{selectedRowKeys.length}</strong> status change
                                request{selectedRowKeys.length > 1 ? "s" : ""}.
                            </p>
                            <p style={{ color: "#888" }}>
                                This reason will be applied to all selected records.
                            </p>
                        </div>

                        <div>
                            <label
                                style={{
                                    display: "block",
                                    marginBottom: "8px",
                                    fontWeight: "500",
                                }}
                            >
                                {isApprovingAction ? "Approval Reason:" : "Rejection Reason:"}{" "}
                                <span style={{ color: "red" }}>*</span>
                            </label>
                            <TextArea
                                rows={4}
                                placeholder={
                                    isApprovingAction
                                        ? "Enter reason for approval..."
                                        : "Enter reason for rejection..."
                                }
                                value={remarks}
                                onChange={(e) => setRemarks(e.target.value)}
                                maxLength={500}
                                showCount
                            />
                        </div>
                    </div>
                ) : (
                    selectedRequest && (
                        <div>
                            <div style={{ marginBottom: "20px" }}>
                                <p>
                                    <strong>Employee:</strong>{" "}
                                    {selectedRequest.employee_name} ({selectedRequest.emp_code})
                                </p>
                                <p>
                                    <strong>Attendance Date:</strong>{" "}
                                    {dayjs(selectedRequest.date).format("DD/MM/YYYY")}
                                </p>
                                <p>
                                    <strong>Current Status:</strong>{" "}
                                    <Tag color={getStatusColor(selectedRequest.status)}>
                                        {selectedRequest.status}
                                    </Tag>
                                </p>
                                <p>
                                    <strong>Requested Status:</strong>{" "}
                                    <Tag color={getStatusColor(selectedRequest.pending_status_change)}>
                                        {selectedRequest.pending_status_change}
                                    </Tag>
                                </p>
                                <p>
                                    <strong>Request Remarks:</strong>{" "}
                                    {selectedRequest.status_change_remarks || "-"}
                                </p>
                                <p>
                                    <strong>Requested By:</strong>{" "}
                                    {selectedRequest.requested_by_name}
                                </p>
                            </div>

                            <div>
                                <label
                                    style={{
                                        display: "block",
                                        marginBottom: "8px",
                                        fontWeight: "500",
                                    }}
                                >
                                    {isApprovingAction ? "Approval Reason:" : "Rejection Reason:"}{" "}
                                    <span style={{ color: "red" }}>*</span>
                                </label>
                                <TextArea
                                    rows={4}
                                    placeholder={
                                        isApprovingAction
                                            ? "Enter reason for approval..."
                                            : "Enter reason for rejection..."
                                    }
                                    value={remarks}
                                    onChange={(e) => setRemarks(e.target.value)}
                                    maxLength={500}
                                    showCount
                                />
                            </div>
                        </div>
                    )
                )}
            </Modal>
        </section>
    );
}